import { useCallback } from 'react'
import type { PanInfo } from 'framer-motion'

/**
 * useSwipeToDismiss — Framer Motion drag-based swipe-to-dismiss helper.
 *
 * Returns prop spreads to attach to a `motion.div` so it can be dragged
 * along one axis and dismissed when the user crosses an offset OR velocity
 * threshold. Designed for bottom-sheets, top-banners, and side-panels.
 *
 * Part of F5.4 — Mobile Experience (APP_FEEL_2027_UPGRADE).
 *
 * @example Bottom sheet
 * const swipe = useSwipeToDismiss({ onDismiss: onClose })
 * <motion.div {...swipe.dragProps}>
 *   <div {...swipe.handleProps}>...drag handle...</div>
 *   ...content...
 * </motion.div>
 *
 * @example Right-side slide-over
 * const swipe = useSwipeToDismiss({ onDismiss: onClose, direction: 'right' })
 */

type Direction = 'bottom' | 'top' | 'right' | 'left'

interface UseSwipeToDismissOptions {
  /** Called when the user completes a dismiss gesture. */
  onDismiss: () => void
  /** Pixel offset on the drag axis required to dismiss. Default 120. */
  offsetThreshold?: number
  /** Velocity (px/s) required to dismiss regardless of offset. Default 600. */
  velocityThreshold?: number
  /**
   * Direction the panel exits to when dismissed. Default 'bottom'.
   * - 'bottom': bottom sheet, swipe down to close
   * - 'top':    top banner, swipe up to close
   * - 'right':  right-side slide-over, swipe right to close
   * - 'left':   left-side slide-over, swipe left to close
   */
  direction?: Direction
  /** Disable the gesture (e.g. when sheet is closed). */
  disabled?: boolean
}

interface UseSwipeToDismissResult {
  /** Spread on the outer `motion.div` for drag behaviour. */
  dragProps: {
    drag: 'y' | 'x' | false
    dragDirectionLock: boolean
    dragConstraints: {
      top: number
      bottom: number
      left: number
      right: number
    }
    dragElastic: { top: number; bottom: number; left: number; right: number }
    dragMomentum: boolean
    onDragEnd: (
      event: PointerEvent | MouseEvent | TouchEvent,
      info: PanInfo,
    ) => void
  }
  /** Optional spread on a visual drag handle (sets touch-action). */
  handleProps: {
    style: { touchAction: 'none' }
  }
}

const AXIS_FOR_DIRECTION: Record<Direction, 'x' | 'y'> = {
  bottom: 'y',
  top: 'y',
  right: 'x',
  left: 'x',
}

export function useSwipeToDismiss({
  onDismiss,
  offsetThreshold = 120,
  velocityThreshold = 600,
  direction = 'bottom',
  disabled = false,
}: UseSwipeToDismissOptions): UseSwipeToDismissResult {
  const axis = AXIS_FOR_DIRECTION[direction]

  const handleDragEnd = useCallback(
    (_event: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
      if (disabled) return
      const offset = axis === 'y' ? info.offset.y : info.offset.x
      const velocity = axis === 'y' ? info.velocity.y : info.velocity.x
      // Positive sign = dismissing toward 'bottom' / 'right'.
      // Negative sign = dismissing toward 'top' / 'left'.
      const isPositiveDirection =
        direction === 'bottom' || direction === 'right'
      const shouldDismiss = isPositiveDirection
        ? offset > offsetThreshold || velocity > velocityThreshold
        : offset < -offsetThreshold || velocity < -velocityThreshold
      if (shouldDismiss) onDismiss()
    },
    [onDismiss, offsetThreshold, velocityThreshold, direction, axis, disabled],
  )

  // Elastic only in the dismiss direction so the panel "rubber-bands" the right way.
  const elastic = { top: 0, bottom: 0, left: 0, right: 0 }
  if (direction === 'bottom') elastic.bottom = 0.6
  else if (direction === 'top') elastic.top = 0.6
  else if (direction === 'right') elastic.right = 0.6
  else if (direction === 'left') elastic.left = 0.6

  return {
    dragProps: {
      drag: disabled ? false : axis,
      dragDirectionLock: true,
      dragConstraints: { top: 0, bottom: 0, left: 0, right: 0 },
      dragElastic: elastic,
      dragMomentum: false,
      onDragEnd: handleDragEnd,
    },
    handleProps: {
      style: { touchAction: 'none' },
    },
  }
}
